import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Loan, Party } from '../api/types'
import { Composer } from '../components/Composer'
import { DealConditions, DealEditor, DealMilestones } from '../components/DealEditor'
import { ExportToLosModal } from '../components/ExportToLosModal'
import { Icon } from '../components/Icon'
import { EnrollmentList, PlaybookModal } from '../components/Playbooks'
import { SendMessageModal } from '../components/SendMessageModal'
import { ErrorState, Loading } from '../components/States'
import { TaskList } from '../components/TaskList'
import { Timeline } from '../components/Timeline'
import { useToast } from '../components/Toast'
import { initials, percent, rate, shortDate, titleCase, usd } from '../lib/format'
import { subjectOfLoan } from '../lib/subject'
import { tint } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

// The milestone rail is the loan's spine. Order is the funnel, not the JSON key
// order; a milestone with no date is a future segment rather than a hidden one.
const RAIL: { key: string; label: string }[] = [
  { key: 'application_taken', label: 'Application' },
  { key: 'pre_approval', label: 'Pre-approved' },
  { key: 'processing', label: 'Processing' },
  { key: 'initial_submission', label: 'Underwriting' },
  { key: 'approved', label: 'Approved' },
  { key: 'clear_to_close', label: 'Clear to close' },
  { key: 'funded', label: 'Funded' },
]

const INTERNAL = ['loan_officer', 'loan_processor', 'loan_assistant']
const BORROWERS = ['borrower', 'co_borrower']

type Tab = 'overview' | 'activity' | 'tasks' | 'conditions' | 'milestones' | 'people' | 'workflows'

function railSegments(loan: Loan) {
  const dates = loan.dates ?? {}
  const reached = RAIL.map((r) => Boolean(dates[r.key]))
  const currentIdx = reached.lastIndexOf(true)
  const blocked = Object.values(loan.processing ?? {})
    .some((c) => c && !c.completed && c.requested_at)

  return RAIL.map((r, i) => {
    const done = reached[i]
    const isCurrent = i === currentIdx
    const isNext = i === currentIdx + 1
    let bar = 'oklch(0.93 0.005 265)'
    let labelColor = 'var(--ink-3)'
    if (done && !isCurrent) { bar = 'var(--positive)'; labelColor = 'var(--ink-2)' }
    else if (isCurrent) { bar = 'var(--accent)'; labelColor = 'var(--ink)' }
    else if (isNext && blocked) { bar = 'oklch(0.9 0.03 55)'; labelColor = 'var(--warning)' }
    return {
      ...r,
      bar,
      labelColor,
      date: dates[r.key] ? shortDate(dates[r.key]) : isNext && blocked ? 'Blocked' : '—',
    }
  })
}

export function LoanFile() {
  const { loanId = '' } = useParams()
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('overview')
  const [bump, setBump] = useState(0)
  const [stageMenu, setStageMenu] = useState(false)
  const [message, setMessage] = useState<{ channel: 'sms' | 'email'; contactId: number } | null>(null)
  const [playbook, setPlaybook] = useState(false)
  const [editing, setEditing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const { data: loan, error, loading, reload } = useAsync(() => api.loan(loanId), [loanId])
  const { data: board } = useAsync(() => api.customize().catch(() => null), [])
  // Lead sources, condition statuses and the common-condition chips — only a deal
  // has anything to edit, so a LendingPad file doesn't pay for the call.
  const { data: vocab } = useAsync(
    () => (loanId.startsWith('deal:') ? api.vocabulary().catch(() => null) : Promise.resolve(null)),
    [loanId])

  if (loading && !loan) return <div className="page"><Loading /></div>
  if (error || !loan) return <div className="page"><ErrorState error={error} onRetry={reload} /></div>

  // A pre-LOS file is known by its id, not by `source`: on a deal that field carries
  // where the lead came from, which is a different question.
  const dealId = loan.deal_id ?? (loan.loan_id.startsWith('deal:') ? Number(loan.loan_id.slice(5)) : null)
  const isDeal = dealId !== null
  const subject = subjectOfLoan(loan.loan_id)
  const parties = loan.parties ?? []
  const borrower = parties.find((p) => p.role === 'borrower')
  const name =
    [borrower?.first_name, borrower?.last_name].filter(Boolean).join(' ') ||
    loan.borrower || loan.loan_number || 'Loan'

  const conditions = Object.entries(loan.processing ?? {})
  const openCount = conditions.filter(([, c]) => !c.completed).length
  const segments = railSegments(loan)
  const stages = (board?.stages ?? []).filter((s) => !s.hidden)
  const currentIdx = stages.findIndex((s) => s.key === loan.status)
  const nextStage = stages.slice(currentIdx + 1).find((s) => !s.terminal)

  const team = parties.filter((p) => INTERNAL.includes(p.role))
  const others = parties.filter((p) => !INTERNAL.includes(p.role) && !BORROWERS.includes(p.role))

  const term = loan.term_months ? `${loan.term_months / 12} yr` : '—'
  const fico = loan.credit_score ? String(loan.credit_score) : '—'
  // A deal has no appraisal, APR or CLTV to show; it does have the intake answers
  // an officer collected, which is what they came to this card to check.
  const terms: [string, string][] = isDeal
    ? [
      ['Loan amount', usd(loan.loan_amount)],
      [loan.purpose === 'purchase' ? 'Purchase price' : 'Estimated value', usd(loan.purchase_price)],
      ['Down payment', usd(loan.down_payment)],
      ['Rate', rate(loan.note_rate)],
      ['LTV', percent(loan.ltv)],
      ['FICO', fico],
      ['Term', term],
      ['Loan type', titleCase(loan.loan_type)],
      ['Occupancy', titleCase(loan.occupancy)],
      ['Property type', titleCase(loan.property_type)],
      ['Co-borrower', loan.co_borrower_name || '—'],
      ['Lock expires', shortDate(loan.dates?.['lock_expiration'])],
      ['Est. close', shortDate(loan.dates?.['estimated_closing'])],
    ]
    : [
      ['Loan amount', usd(loan.loan_amount)],
      ['Purchase price', usd(loan.purchase_price)],
      ['Appraised value', usd(loan.appraisal_value)],
      ['Rate / APR', `${rate(loan.note_rate)} / ${rate(loan.apr)}`],
      ['Lock expires', shortDate(loan.dates?.['lock_expiration'])],
      ['LTV / CLTV', `${percent(loan.ltv)} / ${percent(loan.cltv)}`],
      ['FICO', fico],
      ['Term', term],
      ['Est. close', shortDate(loan.dates?.['estimated_closing'])],
    ]

  async function moveTo(stage: string) {
    setStageMenu(false)
    try {
      const r = await api.moveStage(loan!.loan_id, stage)
      toast(r.stage_moved
        ? `Moved to ${titleCase(stage)} in the CRM. The loan system still says ${titleCase(r.los_status)} — update it there too.`
        : `Moved to ${titleCase(stage)}`, { tone: 'positive' })
      reload()
      setBump((n) => n + 1)
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  function chaseCondition(name: string) {
    api.createTask({ title: `Chase ${name.toLowerCase()}`, due: undefined, subject })
      .then(() => { toast('Task added', { tone: 'positive' }); setBump((n) => n + 1) })
      .catch((e) => toast(errorText(e), { tone: 'danger' }))
  }

  /** Texting someone on a loan file needs a lead-book contact (consent lives there).
   *  The borrower of a pre-LOS deal already has one; a LendingPad party gets one
   *  created on first use — with no consent assumed. */
  async function messageParty(p: Party | null, channel: 'sms' | 'email') {
    try {
      let contactId = p ? p.contact_id ?? null : loan!.contact_id
      if (!contactId && p?.person_key) contactId = (await api.personToContact(p.person_key)).contact_id
      if (!contactId) return toast('No contact details on file for them.', { tone: 'warning' })
      setMessage({ channel, contactId })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  const personHref = (p: Party) =>
    p.contact_id ? `/contacts/c/${p.contact_id}` : p.person_key ? `/partners/${encodeURIComponent(p.person_key)}` : null

  const borrowerTarget: Party | null = borrower ?? null
  const canMessageBorrower = Boolean(loan.contact_id || borrower?.person_key)

  return (
    <div className="page loan-workspace">
      <div className="loan-breadcrumb"><Link to="/pipeline"><Icon name="arrow_back" size={16} /> Pipeline</Link><span>/</span><span>{isDeal ? 'Opportunity' : `Loan ${loan.loan_number || loan.loan_id}`}</span></div>
      <header className="loan-identity">
        <div className="loan-identity-main"><span className="loan-avatar" style={tint(name)}>{initials(name)}</span><div><div className="loan-eyebrow">{titleCase(loan.purpose)} · {titleCase(loan.loan_type)}<span className="badge t-accent">{loan.stage_label || titleCase(loan.status)}</span>{isDeal ? <span className="chip-outline">Pre-LOS</span> : null}</div><h1>{name}</h1><p><Icon name="location_on" size={15} />{[loan.property_street, loan.property_city, loan.property_state, loan.property_zip].filter(Boolean).join(', ') || 'Property to be determined'}</p></div></div>
        <div className="loan-header-actions">{isDeal ? <><button className="btn" onClick={() => setEditing(true)}><Icon name="edit" size={16} /> Edit opportunity</button>
          {/* The way out of the CRM: a 3.4 file LendingPad can open, so an
              application isn't retyped. Only for a pre-LOS opportunity — a loan
              already in LendingPad has nothing to hand over. */}
          <button className="btn los" onClick={() => setExporting(true)}><Icon name="upload_file" size={16} /> Send to LendingPad</button></> : null}
          <div className="loan-stage-control"><button className="btn primary" disabled={!nextStage} onClick={() => nextStage && moveTo(nextStage.key)}>{nextStage ? `Advance to ${nextStage.label}` : 'Final stage'}<Icon name="arrow_forward" size={16} /></button><button className="btn primary" aria-label="Choose a stage" aria-expanded={stageMenu} onClick={() => setStageMenu(!stageMenu)}><Icon name="expand_more" size={18} /></button>
          {stageMenu ? <div className="menu loan-stage-menu">{stages.map(s => <button key={s.key} className={s.key === loan.status ? 'active' : ''} onClick={() => moveTo(s.key)}><Icon name={s.key === loan.status ? 'check' : 'arrow_forward'} size={15} />{s.label}</button>)}</div> : null}</div>
        </div>
      </header>
      {loan.stage_moved ? <div className="loan-los-note"><Icon name="info" size={16} />CRM stage updated. LendingPad currently reports {titleCase(loan.los_status)}.</div> : null}
      <section className="loan-summary-strip" aria-label="Loan at a glance">{[
        ['Loan amount', usd(loan.loan_amount)], ['Interest rate', rate(loan.note_rate)], ['Loan to value', percent(loan.ltv)], ['Estimated closing', shortDate(loan.dates?.['estimated_closing'])],
      ].map(([label,value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</section>
      <section className="loan-file-surface">
        <div className="loan-progress" aria-label="Loan progress">{segments.map((segment,i) => <div className="loan-progress-step" key={segment.key}><span className="loan-progress-node" style={{ background: segment.date !== '—' && segment.date !== 'Blocked' ? segment.bar : undefined, color: segment.date !== '—' && segment.date !== 'Blocked' ? '#fff' : undefined }}>{segment.date !== '—' && segment.date !== 'Blocked' ? <Icon name="check" size={12} /> : String(i+1).padStart(2,'0')}</span><span className="loan-progress-label" style={{color:segment.labelColor}}>{segment.label}<small>{segment.date}</small></span></div>)}</div>
        <div className="loan-file-columns">
          <div className="loan-main-panel">
            <nav className="loan-tabs" aria-label="Loan sections">{([
              ['overview','Overview'],['activity','Activity'],['tasks','Tasks'],['conditions','Conditions'],['milestones','Milestones'],['people','People'],['workflows','Workflows'],
            ] as [Tab,string][]).map(([key,label]) => <button key={key} className={tab === key ? 'active' : ''} aria-current={tab === key ? 'page' : undefined} onClick={() => setTab(key)}>{label}{key === 'conditions' && openCount > 0 ? <span>{openCount}</span> : null}</button>)}</nav>
            <div className="loan-tab-content">
              {tab === 'overview' ? <>
                <div className="loan-section-heading"><div><span className="panel-overline">THE DETAILS</span><h2>Financing overview</h2></div>{isDeal ? <button className="text-action" onClick={() => setEditing(true)}><Icon name="edit" size={15} /> Edit details</button> : <span className="loan-source"><Icon name="lock" size={13} /> LendingPad · read-only</span>}</div>
                <dl className="loan-terms-grid">{terms.map(([label,value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
                <div className="loan-property-summary"><span className="loan-property-icon"><Icon name="home" size={26} /></span><div><h3>Subject property</h3><p>{[loan.property_street,loan.property_city,loan.property_state,loan.property_zip].filter(Boolean).join(', ') || 'No property selected yet'}</p><span>{[loan.property_type,loan.occupancy,loan.program_name].filter(Boolean).map(titleCase).join(' · ') || 'Property details will appear here'}</span></div></div>
                {loan.notes ? <section className="loan-overview-notes"><h3>File notes</h3><p>{loan.notes}</p></section> : null}
              </> : null}
            {tab === 'activity' && (
              <>
                <Composer subject={subject} onLogged={() => setBump((n) => n + 1)} />
                <Timeline subject={subject} refreshKey={bump} emptyText="No notes, calls or stage changes on this file yet." />
              </>
            )}

            {tab === 'tasks' && <TaskList subject={subject} refreshKey={bump} />}

            {tab === 'conditions' && (dealId !== null ? (
              <DealConditions
                dealId={dealId}
                items={conditions}
                common={vocab?.common_conditions ?? []}
                statuses={vocab?.condition_status ?? []}
                onChanged={() => { reload(); setBump((n) => n + 1) }}
                onChase={chaseCondition}
              />
            ) : (
              conditions.length ? (
                conditions.map(([k, c]) => {
                  const blocking = !c.completed && c.requested_at
                  const icon = c.completed ? 'check_circle' : blocking ? 'error' : 'schedule'
                  const color = c.completed ? 'var(--positive)' : blocking ? 'var(--danger)' : 'var(--ink-4)'
                  return (
                    <div className="condrow" key={k}>
                      <Icon name={icon} size={19} style={{ color }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{titleCase(k)}</span>
                        <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                          {c.completed
                            ? `Received ${shortDate(c.received_at)}`
                            : c.requested_at
                              ? `Requested ${shortDate(c.requested_at)} · blocks underwriting`
                              : 'Not requested'}
                        </span>
                      </span>
                      {!c.completed ? (
                        <button className="btn sm" title="Create a follow-up task for this condition"
                          onClick={() => api.createTask({ title: `Chase ${titleCase(k).toLowerCase()}`, due: undefined, subject })
                            .then(() => { toast('Task added', { tone: 'positive' }); setBump((n) => n + 1) })
                            .catch((e) => toast(errorText(e), { tone: 'danger' }))}>
                          <Icon name="add_task" size={15} />
                        </button>
                      ) : null}
                      <span className={`badge ${c.completed ? 't-positive' : blocking ? 't-danger' : 't-neutral'}`}>
                        {c.completed ? 'Cleared' : blocking ? 'Blocking' : 'Waiting'}
                      </span>
                    </div>
                  )
                })
              ) : <p className="quiet">No conditions tracked on this file.</p>
            ))}

            {tab === 'milestones' && (dealId !== null ? (
              <DealMilestones dealId={dealId} dates={loan.dates} onSaved={reload} />
            ) : (
              Object.entries(loan.dates ?? {}).length ? (
                Object.entries(loan.dates ?? {})
                  .sort((a, b) => new Date(a[1]).getTime() - new Date(b[1]).getTime())
                  .map(([k, v]) => (
                    <div className="kvrow" key={k}>
                      <span className="k">{titleCase(k)}</span>
                      <span className="v">{shortDate(v)}</span>
                    </div>
                  ))
              ) : <p className="quiet">No milestone dates yet.</p>
            ))}
              {tab === 'people' ? <div className="loan-people-sections">          {!isDeal ? (
            <section className="card">
              <div className="card-head"><h2 className="h2">Deal team</h2></div>
              <div className="card-body" style={{ paddingTop: 4 }}>
                {team.length ? team.map((p, i) => (
                  <div className="teamrow" key={`${p.role}-${i}`}>
                    <span className="circle-av" style={tint(p.full_name)}>{initials(p.full_name)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="teamname" style={{ display: 'block' }}>{p.full_name}</span>
                      <span className="teamrole">{titleCase(p.role)}</span>
                    </span>
                    {p.email ? (
                      <a href={`mailto:${p.email}`} className="icon-btn-plain" title={`Email ${p.full_name}`}><Icon name="mail" size={17} /></a>
                    ) : null}
                  </div>
                )) : <p className="quiet">No internal assignments on this file.</p>}
              </div>
            </section>
          ) : null}
          <section className="card">
            <div className="card-head"><h2 className="h2">{isDeal ? 'People' : 'Third parties'}</h2></div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {others.length ? others.map((p: Party, i) => {
                const who = p.full_name || p.company || '—'
                const to = personHref(p)
                return (
                  <div className="teamrow" key={`${p.role}-${i}`}>
                    <span className="circle-av sm" style={tint(who)}>{initials(who)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {to ? <Link to={to} className="teamname" style={{ display: 'block' }}>{who}</Link>
                        : <span className="teamname" style={{ display: 'block' }}>{who}</span>}
                      <span className="teamrole">
                        {titleCase(p.role)}{p.company && p.full_name ? ` · ${p.company}` : ''}
                      </span>
                    </span>
                    {p.email && p.person_key ? (
                      <button className="icon-btn-plain" title={`Email ${who}`} onClick={() => messageParty(p, 'email')}>
                        <Icon name="mail" size={17} />
                      </button>
                    ) : null}
                  </div>
                )
              }) : <p className="quiet">{isDeal ? 'Agents and title appear once the loan system has the file.' : 'No third parties on this file.'}</p>}
            </div>
          </section></div> : null}
              {tab === 'workflows' ? <><div className="loan-section-heading"><div><span className="panel-overline">KEEP THINGS MOVING</span><h2>Workflows on this file</h2></div><button className="btn" onClick={() => setPlaybook(true)}><Icon name="add" size={15} /> Start workflow</button></div><EnrollmentList subject={subject} refreshKey={bump} onChange={() => setBump(n => n+1)} /></> : null}
            </div>
          </div>
          <aside className="loan-context-panel">
            <section className="loan-borrower-section"><span className="panel-overline">YOUR BORROWER</span><div className="loan-borrower-name"><span className="circle-av" style={tint(name)}>{initials(name)}</span><strong>{name}</strong></div>
              {borrower?.email ? <a className="loan-contact-line" href={`mailto:${borrower.email}`}><Icon name="mail" size={15} />{borrower.email}</a> : null}
              {borrower?.mobile_phone || borrower?.home_phone ? <a className="loan-contact-line" href={`tel:${(borrower.mobile_phone ?? borrower.home_phone ?? '').replace(/[^\d+]/g,'')}`}><Icon name="call" size={15} />{borrower.mobile_phone || borrower.home_phone}</a> : null}
              <div className="loan-contact-actions"><button className="btn" disabled={!canMessageBorrower} onClick={() => messageParty(borrowerTarget,'sms')}><Icon name="sms" size={16} /> Text</button><button className="btn" disabled={!canMessageBorrower} onClick={() => messageParty(borrowerTarget,'email')}><Icon name="mail" size={16} /> Email</button></div>
              {loan.contact_id ? <Link className="loan-profile-link" to={`/contacts/c/${loan.contact_id}`}>View contact profile <Icon name="north_east" size={14} /></Link> : borrower?.person_key ? <Link className="loan-profile-link" to={`/partners/${encodeURIComponent(borrower.person_key)}`}>View borrower profile <Icon name="north_east" size={14} /></Link> : null}
            </section>
            <section className="loan-readiness"><span className="panel-overline">FILE READINESS</span><div className="loan-readiness-title"><Icon name={openCount ? 'pending_actions' : 'check_circle'} size={20} /><h3>{openCount ? `${openCount} open condition${openCount === 1 ? '' : 's'}` : conditions.length ? 'Conditions cleared' : 'No conditions tracked'}</h3></div><p>{openCount ? 'Review the outstanding items to keep this file moving.' : conditions.length ? 'All tracked conditions are complete.' : 'Conditions will appear here as the file progresses.'}</p><button className="text-action" onClick={() => setTab('conditions')}>Review conditions <Icon name="arrow_forward" size={15} /></button></section>
            <section className="loan-key-dates"><span className="panel-overline">COMING UP</span><div><span>Rate lock expires</span><strong>{shortDate(loan.dates?.['lock_expiration'])}</strong></div><div><span>Estimated close</span><strong>{shortDate(loan.dates?.['estimated_closing'])}</strong></div></section>
            <section className="loan-context-workflow"><button className="btn" onClick={() => setPlaybook(true)}><Icon name="route" size={17} /> Start a workflow <Icon name="add" size={15} /></button><p>{isDeal ? 'Created in your CRM' : 'Synced from LendingPad'}<br />Updated {shortDate(loan.updated_at ?? loan.status_at)}</p></section>
          </aside>
        </div>
      </section>

      {message ? (
        <SendMessageModal open channel={message.channel} contactId={message.contactId}
          context={isDeal ? undefined : { loan_id: loan.loan_id }}
          onClose={() => setMessage(null)} onSent={() => setBump((n) => n + 1)} />
      ) : null}
      {exporting && dealId !== null ? (
        <ExportToLosModal subject={{ deal_id: dealId }} onClose={() => setExporting(false)}
          onPushed={() => { reload(); setBump((n) => n + 1) }} />
      ) : null}
      {editing && dealId !== null ? (
        <DealEditor loan={loan} dealId={dealId} sources={vocab?.source ?? []}
          onClose={() => setEditing(false)} onSaved={() => { reload(); setBump((n) => n + 1) }} />
      ) : null}
      <PlaybookModal open={playbook} onClose={() => setPlaybook(false)} subject={subject} onEnrolled={() => setBump((n) => n + 1)}
        firstName={borrower?.first_name || name.split(' ')[0]} />
    </div>
  )
}
